import React, { useState } from 'react';
import Sidebar from '../Sidebar/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faComments, 
  faCalendarAlt, 
  faExclamationCircle, 
  faSearch,
  faPaperPlane,
  faHistory,
  faUser,
  faEnvelope,
  faTimes,
  faReply
} from '@fortawesome/free-solid-svg-icons';
import { useMessages } from '../../context/MessageContext';
import { Howl } from 'howler';
import { buildApiUrl } from '../../config/api.config';

const emailSend = new Howl({ src: ['/sounds/email.mp3'] });
const cancelButton = new Howl({ src: ['/sounds/cancel.mp3'] });

const Reviews = () => {
  const { messages, loading } = useMessages();
  const [searchTerm, setSearchTerm] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleReplyClick = (messageId) => {
    setReplyingTo(replyingTo === messageId ? null : messageId);
    setReplyContent('');
  };

  const handleViewHistory = (message) => {
    setSelectedMessage(message);
    setShowHistory(true);
  };

  const handleSendReply = async (messageId) => {
    if (!replyContent.trim()) {
      setErrorMessage('Reply content cannot be empty');
      return;
    }

    setIsSending(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('message/send-reply'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify({
          messageId,
          replyContent
        })
      });
      
      if (!response.ok) throw new Error('Failed to send reply');
      
      emailSend.play();
      setReplyingTo(null);
      setReplyContent('');
    } catch (error) {
      setErrorMessage(error.message || 'Failed to send reply');
    } finally {
      setIsSending(false);
    }
  };

  const filteredMessages = messages.filter(message => 
    message.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const ClickCancel = () => {
    setReplyingTo(null);
    cancelButton.play();
  };

  return (
    <div className="admin-layout">
      <Sidebar />
      <main>
        <div className="tf-card" style={{ padding: '0', overflow: 'hidden' }}>
          <header className="tf-page-header" style={{ padding: '2rem 2rem 0 2rem', marginBottom: '2rem' }}>
            <div className="tf-header-content">
              <div>
                <h1 className="tf-page-title">Customer Messages</h1>
                <p className="tf-page-subtitle">Engage with your customers and manage inquiries</p>
              </div>
              
              <div className="tf-search-container">
                <FontAwesomeIcon icon={faSearch} className="tf-search-icon" />
                <input 
                  className="tf-search-input"
                  type="text" 
                  placeholder="Find a specific message..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </header>

          <div style={{ padding: '0 2rem' }}>
            <div className="tf-status-grid">
              <div className="tf-stat-card">
                <div className="tf-stat-icon-box tf-stat-icon-primary">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <div className="tf-stat-content">
                  <h4>{messages.length}</h4>
                  <p>Total Archive</p>
                </div>
              </div>
              <div className="tf-stat-card">
                <div className="tf-stat-icon-box tf-stat-icon-success">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
                <div className="tf-stat-content">
                  <h4>{messages.filter(m => new Date(m.date).toDateString() === new Date().toDateString()).length}</h4>
                  <p>Received Today</p>
                </div>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div style={{ margin: '0 2rem 2rem 2rem', padding: '1rem 1.5rem', borderRadius: '8px', background: 'rgba(255, 56, 96, 0.1)', color: 'var(--tf-error)', fontSize: '0.9rem', fontWeight: '500', border: '1px solid rgba(255, 56, 96, 0.2)' }}>
              <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '0.75rem' }} />
              {errorMessage}
            </div>
          )}

          {loading ? (
            <div className="tf-flex-center" style={{ padding: '6rem 2rem', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="tf-spinner"></div>
              <p style={{ color: 'var(--tf-text-muted)', fontWeight: '500' }}>Synchronizing messages...</p>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="tf-empty-state">
              <FontAwesomeIcon icon={faExclamationCircle} size="3x" style={{ marginBottom: '1.5rem', opacity: 0.1 }} />
              <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>{searchTerm ? 'No matching messages found' : 'Your message inbox is currently empty'}</p>
            </div>
          ) : (
            <div className="tf-list-responsive">
              <div className="tf-list-header-row" style={{ gridTemplateColumns: '1.5fr 1.5fr 1fr 2fr 100px', margin: '0 1.5rem 0.5rem 1.5rem' }}>
                <span>CUSTOMER</span>
                <span>EMAIL</span>
                <span>DATE</span>
                <span>MESSAGE PREVIEW</span>
                <span style={{ textAlign: 'right' }}>ACTION</span>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {filteredMessages.map((message) => (
                  <div key={message._id} className="tf-list-item-row" style={{ 
                    gridTemplateColumns: '1.5fr 1.5fr 1fr 2fr 100px', 
                    padding: '1.25rem 1.5rem',
                    borderLeft: message.status === 'pending' ? '4px solid var(--tf-primary)' : '4px solid transparent',
                    background: message.status === 'pending' ? 'rgba(255, 65, 53, 0.02)' : 'transparent'
                  }}>
                    <div className="tf-flex" style={{ gap: '1rem' }}>
                      <div className="tf-avatar tf-avatar-sm" style={{ background: 'var(--tf-bg-input)' }}>
                        {message.firstName.charAt(0)}{message.lastName.charAt(0)}
                      </div>
                      <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{message.firstName} {message.lastName}</span>
                    </div>
                    <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>{message.email}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--tf-text-muted)' }}>
                      {new Date(message.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div style={{ color: 'var(--tf-text-white)', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {message.message}
                    </div>
                    <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <button 
                         className="tf-btn"
                         style={{ width: '32px', height: '32px', padding: 0, background: 'rgba(255,255,255,0.05)', color: 'var(--tf-text-muted)' }}
                         title="View History"
                         onClick={() => handleViewHistory(message)}
                      >
                         <FontAwesomeIcon icon={faHistory} size="sm" />
                      </button>
                      <button 
                         className="tf-btn"
                         style={{ width: '32px', height: '32px', padding: 0, background: replyingTo === message._id ? 'var(--tf-primary)' : 'rgba(255, 65, 53, 0.1)', color: replyingTo === message._id ? 'white' : 'var(--tf-primary)' }}
                         title="Reply"
                         onClick={() => handleReplyClick(message._id)}
                      >
                         <FontAwesomeIcon icon={faReply} size="sm" />
                      </button>
                    </div>

                    {replyingTo === message._id && (
                      <div style={{ gridColumn: '1 / -1', marginTop: '1rem', padding: '1.5rem', background: 'var(--tf-bg-input)', borderRadius: 'var(--tf-radius-md)', border: '1px solid var(--tf-border)' }}>
                        <div className="tf-flex" style={{ gap: '0.75rem', marginBottom: '1rem', color: 'var(--tf-primary)' }}>
                          <FontAwesomeIcon icon={faReply} />
                          <span style={{ fontWeight: '700', fontSize: '0.85rem', textTransform: 'uppercase' }}>Compose Response</span>
                        </div>
                        <textarea
                          className="tf-input"
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Type your professional response here..."
                          rows="4"
                          style={{ marginBottom: '1rem', resize: 'vertical' }}
                        />
                        <div className="tf-flex" style={{ justifyContent: 'flex-end', gap: '1rem' }}>
                          <button className="tf-btn tf-btn-outline" style={{ padding: '0.5rem 1.5rem', fontSize: '0.85rem' }} onClick={ClickCancel} disabled={isSending}>
                            Discard
                          </button>
                          <button className="tf-btn tf-btn-primary" style={{ padding: '0.5rem 1.5rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={() => handleSendReply(message._id)} disabled={isSending}>
                            {isSending ? 'Sending...' : <><FontAwesomeIcon icon={faPaperPlane} /> Send Reply</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {showHistory && selectedMessage && (
        <div className="tf-modal-overlay">
          <div className="tf-modal" style={{ maxWidth: '600px' }}>
            <div className="tf-modal-body" style={{ padding: '0', display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
              <div className="tf-flex" style={{ justifyContent: 'space-between', padding: '1.5rem', borderBottom: '1px solid var(--tf-border)' }}>
                <h3 className="tf-heading-sm" style={{ margin: 0 }}>Conversation Detail</h3>
                <button onClick={() => setShowHistory(false)} className="tf-modal-close" style={{ position: 'static', margin: 0 }}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className="tf-sidebar-scroll-area" style={{ padding: '1.5rem', overflowY: 'auto' }}>
                 <div style={{ marginBottom: '2rem' }}>
                   <div style={{ fontSize: '0.75rem', color: 'var(--tf-primary)', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Customer Query</div>
                   <div style={{ color: 'var(--tf-text-white)', lineHeight: '1.6', fontSize: '0.95rem', background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '8px' }}>"{selectedMessage.message}"</div>
                 </div>

                 <div style={{ fontSize: '0.75rem', color: 'var(--tf-text-muted)', fontWeight: '700', textTransform: 'uppercase', marginBottom: '1rem' }}>Reply History</div>

                 {selectedMessage.replies && selectedMessage.replies.length > 0 ? (
                   <div className="tf-grid" style={{ gap: '1rem' }}>
                     {selectedMessage.replies.map((reply, index) => (
                       <div key={index} style={{ padding: '1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', borderLeft: '3px solid var(--tf-success)' }}>
                         <div className="tf-flex" style={{ justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                           <span style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--tf-success)' }}>
                             <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.4rem' }} />
                             {reply.adminName || 'Admin'}
                           </span>
                           <span style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)' }}>
                             {new Date(reply.date).toLocaleString()}
                           </span>
                         </div>
                         <div style={{ fontSize: '0.9rem', color: 'var(--tf-text-muted)', lineHeight: '1.5' }}>{reply.content}</div>
                       </div>
                     ))}
                   </div>
                 ) : (
                   <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--tf-text-muted)', fontStyle: 'italic', background: 'rgba(255,255,255,0.01)', borderRadius: '8px' }}>
                     No replies have been sent for this message yet.
                   </div>
                 )}
              </div>
              
              <div className="tf-modal-footer">
                 <button className="tf-btn tf-btn-primary" onClick={() => setShowHistory(false)}>Close Overview</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;